import { useEffect, useState } from "react";
import { api, AppConfig, ApiError } from "../api/client";
import { useToast } from "../ui/toast";

/**
 * Procedura zgłaszania (ENISA SRP) — INTERNAL owner runbook for reporting actively-exploited
 * vulnerabilities and severe incidents under CRA Art. 14. Editable here; NEVER published (the
 * public /config strips it — reachable only via the authenticated /owner/config).
 */
export function SrpProcedure() {
  const toast = useToast();
  const [cfg, setCfg] = useState<AppConfig | null>(null);
  const [original, setOriginal] = useState<AppConfig | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getConfig()
      .then((c) => { setCfg(c); setOriginal(c); })
      .catch((e: ApiError) => toast(e.status === 403 ? "Brak dostępu (owner-only)." : e.message));
  }, [toast]);

  if (!cfg || !original) return <div className="card empty">Ładowanie…</div>;
  const changed =
    cfg.srpProcedure.text !== original.srpProcedure.text ||
    (cfg.srpProcedure.textEn ?? "") !== (original.srpProcedure.textEn ?? "");

  const save = async () => {
    setBusy(true);
    try {
      const saved = await api.saveConfig(cfg);
      setCfg(saved);
      setOriginal(saved);
      toast(`Zapisano procedurę SRP (wersja ${saved.srpProcedure.version})`);
    } catch (e) {
      toast((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: 20, maxWidth: 860 }}>
      <div className="section-title" style={{ marginBottom: 4 }}>Procedura zgłaszania (ENISA SRP)</div>
      <div className="section-hint">
        <b>Dokument wewnętrzny — nie jest publikowany.</b> Runbook zgłaszania aktywnie wykorzystywanych
        podatności i poważnych incydentów do platformy ENISA (Single Reporting Platform) zgodnie z CRA
        (art. 14). Obowiązek od <b>11 września 2026</b>. Uzupełnij pola <code>[DO UZUPEŁNIENIA]</code>
        (właściciel procedury, konto SRP, daty). Terminy potwierdź z aktami wykonawczymi / radcą prawnym.
      </div>

      <div className="field" style={{ marginTop: 14 }}>
        <label className="label">Treść procedury (wewnętrzna)</label>
        <textarea
          className="textarea"
          style={{ minHeight: 420 }}
          value={cfg.srpProcedure.text}
          placeholder="Wpisz wewnętrzną procedurę zgłaszania do ENISA SRP…"
          onChange={(e) => setCfg({ ...cfg, srpProcedure: { ...cfg.srpProcedure, text: e.target.value } })}
        />
      </div>

      <div className="field" style={{ marginTop: 14 }}>
        <label className="label">Wersja angielska (EN) — wewnętrzna, do dokumentacji/audytu</label>
        <textarea
          className="textarea"
          style={{ minHeight: 420 }}
          value={cfg.srpProcedure.textEn ?? ""}
          placeholder="Enter the English version of the internal SRP reporting procedure…"
          onChange={(e) => setCfg({ ...cfg, srpProcedure: { ...cfg.srpProcedure, textEn: e.target.value } })}
        />
      </div>

      <div className="row" style={{ marginTop: 12 }}>
        <span className="faint" style={{ fontSize: 12 }}>Aktualna wersja: {original.srpProcedure.version}</span>
        {changed && (
          <span className="badge st-expiring" style={{ marginLeft: 8 }}>
            <span className="dot dot-expiring" />niezapisane zmiany
          </span>
        )}
        <div className="topbar-spacer" />
        <button className="btn primary" disabled={busy || !changed} onClick={save}>Zapisz procedurę</button>
      </div>
    </div>
  );
}
